import type {
  ProductCardItem,
  NormalizedLabel,
  CurrencyConfig,
  NameExtractor,
  NameMapping,
} from './types';

/**
 * Декодирует HTML сущности в тексте
 */
export function decodeHtmlEntities(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/**
 * Получает значение по пути в объекте
 */
function getValueByPath(obj: any, path: string[]): any {
  let current = obj;
  for (const key of path) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = current[key];
  }
  return current;
}

/**
 * Создает функцию для извлечения названия товара с учетом языка
 *
 * @param mapping - Опциональный маппинг путей к полям для разных языков.
 *                  Например: { ru: ['languages', 'ru', 'name'], en: ['name'] }
 * @returns Функция для извлечения названия
 */
export function createNameExtractor(mapping?: NameMapping): NameExtractor {
  return (item: ProductCardItem, langCode: string): string | undefined => {
    // Если есть кастомный маппинг для этого языка, используем его
    if (mapping && mapping[langCode]) {
      const path = mapping[langCode];
      const value = getValueByPath(item, path);
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    // Дефолтная логика поиска названия
    const fallbackOrder = [
      // Попытка найти в languages.{langCode}.name
      () => {
        const langData = item?.languages?.[langCode];
        if (langData?.name && typeof langData.name === 'string' && langData.name.trim()) {
          return langData.name.trim();
        }
        return undefined;
      },
      // Попытка найти в name
      () => {
        if (item?.name && typeof item.name === 'string' && item.name.trim()) {
          return item.name.trim();
        }
        return undefined;
      },
    ];

    // Пробуем каждый способ получения названия
    for (const getter of fallbackOrder) {
      const result = getter();
      if (result) {
        return result;
      }
    }

    return undefined;
  };
}

/**
 * Форматирует цену с учетом валюты и языка
 *
 * @param price - Цена
 * @param currency - Конфигурация валюты
 * @param langCode - Код языка для локализации числа
 * @returns Отформатированная строка с ценой
 */
export function formatPrice(
  price: number | string | null | undefined,
  currency?: CurrencyConfig,
  langCode: string = 'ru'
): string {
  const numericPrice =
    typeof price === 'number'
      ? price
      : typeof price === 'string' && price.trim() !== ''
      ? Number(price)
      : Number.NaN;
  if (!Number.isFinite(numericPrice) || numericPrice < 0) {
    return '';
  }

  // Определяем локаль для форматирования числа
  const locale = langCode === 'ru' ? 'ru-RU' : langCode === 'de' ? 'de-DE' : 'en-US';

  // Если валюта указана, используем Intl.NumberFormat для правильного форматирования
  if (currency?.code) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numericPrice);
    } catch {
      // Fallback если валюта не поддерживается
    }
  }

  // Форматируем число
  const formattedNumber = numericPrice.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  // Если валюта не указана, возвращаем только число
  if (!currency) {
    return formattedNumber;
  }

  // Форматируем с символом валюты согласно позиции (fallback)
  if (currency.position === 'before') {
    return `${currency.symbol}${formattedNumber}`;
  } else {
    return `${formattedNumber} ${currency.symbol}`;
  }
}

export function isPriceOnRequest(price: number | string | null | undefined): boolean {
  const numericPrice =
    typeof price === 'number'
      ? price
      : typeof price === 'string' && price.trim() !== ''
      ? Number(price)
      : Number.NaN;

  return Number.isFinite(numericPrice) && numericPrice === 0;
}

export function formatCatalogPrice(
  price: number | string | null | undefined,
  currency?: CurrencyConfig,
  langCode: string = 'ru',
  onRequestLabel: string = 'По запросу'
): string {
  if (isPriceOnRequest(price)) {
    return onRequestLabel;
  }

  return formatPrice(price, currency, langCode);
}

/**
 * Нормализует labels для отображения на карточке товара
 */
export function normalizeLabels(item: ProductCardItem): NormalizedLabel[] {
  const primary = Array.isArray(item?.labels) ? item.labels : [];
  const combined = [...primary];

  const dedupe = new Map<string, NormalizedLabel>();

  combined.forEach((label) => {
    if (!label || typeof label !== 'object') {
      return;
    }

    const code = label.code ? String(label.code) : '';
    const source = label.source ? String(label.source) : 'product';
    const meta = label.meta && typeof label.meta === 'object' ? label.meta : {};
    const rawValue = label.value ?? label.name ?? meta.name ?? '';
    const valueString = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
    const trimmedValue = valueString.trim();

    if (!trimmedValue) {
      return;
    }

    const metaKey =
      meta.xml_id || meta.enum_id || meta.raw || trimmedValue;
    const dedupeKey = `${code}|${metaKey}|${source}`;

    if (dedupe.has(dedupeKey)) {
      return;
    }

    const normalizedMeta: Record<string, any> = {};
    Object.entries(meta).forEach(([metaKeyName, metaValue]) => {
      if (metaValue === null || metaValue === undefined) {
        return;
      }
      if (typeof metaValue === 'boolean') {
        normalizedMeta[metaKeyName] = metaValue;
        return;
      }
      normalizedMeta[metaKeyName] = String(metaValue);
    });

    dedupe.set(dedupeKey, {
      code,
      source,
      value: trimmedValue,
      name: label.name ? String(label.name) : code,
      meta: normalizedMeta,
    });
  });

  return Array.from(dedupe.values());
}
